import React, { useContext } from 'react'
import PropTypes from 'prop-types'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import usePublicationProduct, { usePublicationProductForm } from '@hooks/usePublicationProduct'

const createOrUpdatePublicationProduct = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: {
      closeModal,
      createPublicationProduct,
      updatePublicationProduct,
    },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const publicationProductPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Publication Product ${text}`,
  }

  if (entityState.id) return updatePublicationProduct(publicationProductPayload)

  return createPublicationProduct(publicationProductPayload)
}

const CreateOrEditPublicationProductModal = (props) => {
  const {
    callbacks, modalKey, showModal,
  } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { publicationProduct } = modalPayload

  const publicationProductPayload = usePublicationProduct(publicationProduct)
  const {
    callbacks: {
      createPublicationProduct: createFn,
      updatePublicationProduct: updateFn,
    },
    creating,
    updating,
  } = publicationProductPayload

  const publicationProductFormPayload = usePublicationProductForm(publicationProduct, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      bleed,
      columns,
      height,
      id,
      product_code,
      rows,
      title,
      width,
    },
    errors,
    handlers,
    saveEnabled,
  } = publicationProductFormPayload

  const saveButtonText = id ? 'Update Product' : 'Create Product'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="Product" />

      <SidebarModal.Content>
        <Form.Field errorMessage={errors.title} label="* Title">
          <input name="title" value={title} type="text" {...handlers} />
        </Form.Field>

        <Form.Field label="Product Code" marginTop="large">
          <input name="product_code" value={product_code} type="text" {...handlers} />
        </Form.Field>

        <Form.Row>
          <Form.Field
            errorMessage={errors.rows}
            label="* Grid Rows"
            marginTop="large"
            hideErrorMessage
          >
            <input data-value-type="integer" name="rows" value={rows} type="text" {...handlers} />
          </Form.Field>

          <Form.Field
            errorMessage={errors.columns}
            label="* Grid Columns"
            marginTop="large"
            hideErrorMessage
          >
            <input data-value-type="integer" name="columns" value={columns} type="text" {...handlers} />
          </Form.Field>
        </Form.Row>

        <Form.Row>
          <Form.Field errorMessage={errors.width} label="* Width (mm)" marginTop="large" hideErrorMessage>
            <input name="width" value={width} type="number" {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.height} label="* Height (mm)" marginTop="large" hideErrorMessage>
            <input name="height" value={height} type="number" {...handlers} />
          </Form.Field>
        </Form.Row>

        <Form.Field errorMessage={errors.bleed} label="* Bleed (mm)" marginTop="large">
          <input name="bleed" value={bleed} type="number" {...handlers} />
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdatePublicationProduct(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditPublicationProductModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditPublicationProductModal.defaultProps = {
  modalKey: 'CreateOrEditPublicationProductModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPublicationProductModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
